'use client'

import { recordActivationImpression } from '@/actions/activation-tracking'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { getActivationTrackingSessionId } from './activation-tracking-client'

export default function useActivationImpression({ activationId, placement }) {
  const pathname = usePathname()
  const trackingRef = useRef(null)
  const visibilityTimeoutRef = useRef(null)
  const isVisibleRef = useRef(false)
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false)

  useEffect(() => {
    setHasTrackedImpression(false)
  }, [activationId])

  useEffect(() => {
    if (!activationId || !trackingRef.current || hasTrackedImpression) {
      return
    }

    const clearVisibilityTimeout = () => {
      if (visibilityTimeoutRef.current) {
        window.clearTimeout(visibilityTimeoutRef.current)
        visibilityTimeoutRef.current = null
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting

        if (!entry.isIntersecting) {
          clearVisibilityTimeout()
          return
        }

        if (visibilityTimeoutRef.current || hasTrackedImpression) {
          return
        }

        visibilityTimeoutRef.current = window.setTimeout(() => {
          if (!isVisibleRef.current || hasTrackedImpression) {
            return
          }

          recordActivationImpression({
            activationId,
            page: pathname,
            placement,
            sessionId: getActivationTrackingSessionId(),
          }).catch((error) => {
            console.error('Failed to record activation impression:', error)
          })

          setHasTrackedImpression(true)
          visibilityTimeoutRef.current = null
        }, 1000)
      },
      {
        threshold: 0.5,
        rootMargin: '0px',
      },
    )

    observer.observe(trackingRef.current)

    return () => {
      clearVisibilityTimeout()
      isVisibleRef.current = false
      observer.disconnect()
    }
  }, [activationId, hasTrackedImpression, pathname, placement])

  return trackingRef
}
